import {
  Box,
  Stack,
  Checkbox,
  TextField,
  InputAdornment,
  Typography,
  Paper,
  Avatar,
} from "@mui/material";
import { ItemListProps } from "./types";
import { useTranslation } from "../../../../../hooks/useTranslation";

export const ItemList = ({
  orderItems,
  includedItems,
  quantities,
  prices,
  onIncludeChange,
  onQuantityChange,
  onPriceChange,
  getMaxQuantity,
  isQuantityValid,
  isPriceValid,
}: ItemListProps) => {
  const { translate } = useTranslation("pages.orderDetails.prepareShipment");

  return (
    <Stack spacing={1.25}>
      {orderItems.map((item) => {
        const included = includedItems[item.id];
        const qtyValue = quantities[item.id];
        const maxQty = getMaxQuantity(item);

        const showQuantityError = included && !isQuantityValid(item, qtyValue);

        return (
          <Paper
            key={item.id}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              opacity: included ? 1 : 0.45,
              transition: "opacity .15s ease",
            }}
          >
            {/* ================= STATIC INFO (AYNEN KORUNDU) ================= */}
            <Stack direction="row" justifyContent="space-between" spacing={1.5}>
              <Checkbox
                checked={included}
                onChange={(e) => onIncludeChange(item.id, e.target.checked)}
                disableRipple
                sx={{
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              />

              <Avatar
                variant="rounded"
                src={item.imageUrl}
                alt={item.productName}
                sx={{ width: 56, height: 56 }}
              />

              <Typography
                fontWeight={600}
                lineHeight={1.3}
                sx={{
                  maxWidth: 300,
                  whiteSpace: "wrap",
                  overflow: "hidden",
                }}
                title={item.productName}
              >
                {item.productName}
              </Typography>

              <Box>
                <Stack spacing={0.25} mt={0.75}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    minWidth={220}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {translate("totalQuantity")}
                    </Typography>
                    <Typography variant="body2">{item.quantity}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {translate("quantityReceived")}
                    </Typography>
                    <Typography variant="body2">
                      {item.receivedQuantity}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {translate("quantityShipped")}
                    </Typography>
                    <Typography variant="body2">
                      {item.quantityShipped}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              <Box minWidth={120} textAlign="right">
                <Typography variant="body2" color="text.secondary">
                  {translate("currentPrice")}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  ${item.unitPrice.toFixed(2)}
                </Typography>
              </Box>
            </Stack>

            {/* ================= EDITABLE AREA ================= */}
            <Paper
              sx={{
                mt: 2,
                ml: 5,
                p: 1.5,
                borderRadius: 1.5,
              }}
              component={Stack}
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="flex-end"
              alignItems="center"
            >
              {showQuantityError && (
                <Typography variant="body1" color="error.main">
                  {translate("quantityExceedsMax", {
                    max: maxQty,
                  })}
                </Typography>
              )}

              <TextField
                label={translate("quantityToShip")}
                size="small"
                type="number"
                value={qtyValue}
                disabled={!included}
                error={showQuantityError}
                onChange={(e) => onQuantityChange(item.id, e.target.value)}
                sx={{ width: 160 }}
                slotProps={{
                  htmlInput: {
                    min: 0,
                    step: 1,
                    max: maxQty,
                  },
                }}
              />

              <TextField
                label={translate("newPrice")}
                size="small"
                type="number"
                value={prices[item.id]}
                disabled={!included}
                error={included && !isPriceValid(prices[item.id])}
                onChange={(e) => onPriceChange(item.id, e.target.value)}
                sx={{ width: 160 }}
                slotProps={{
                  htmlInput: { step: 0.01, min: 0 },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
              />
            </Paper>
          </Paper>
        );
      })}
    </Stack>
  );
};
